import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { DashboardLayout } from "../../../components/layout";
import FreightAdminTableToolbar from "../../../components/admin/FreightAdminTableToolbar";
import FreightSocialMediaDtActions from "../../../components/admin/FreightSocialMediaDtActions";
import { AdminDataTable } from "../../../components/datatable";
import { FREIGHT_SOCIAL_MEDIA_COLUMNS } from "../../../config/freightSocialMediaAdminColumns";
import { ROLE_LABELS } from "../../../config/roles";
import { getRole } from "../../../auth/roleService";
import { useFreightAdminTablePrefs } from "../../../hooks/useFreightAdminTablePrefs";
import { changeFreightSocialMediaStatus, getFreightSocialMediaIndex } from "../../../utils/api";
import { freightNamedListFromResponse } from "../../../utils/freightAdminListResponse";
import { showError, showSuccess } from "../../../utils/toast";
import "./adminFreight.css";

const TABLE_ID = "freight_social_media_v1";
const COLUMN_DEFS = FREIGHT_SOCIAL_MEDIA_COLUMNS;
const LIST_KEY = "socialMediaList";

const sortRows = (list) =>
  [...list].sort((a, b) => {
    const o = Number(a.order_no) - Number(b.order_no);
    if (o !== 0) return o;
    return String(a.name || "").localeCompare(String(b.name || ""));
  });

const FreightSocialMediaPage = () => {
  const navigate = useNavigate();
  const roleLabel = ROLE_LABELS[getRole()] || "Admin";
  const breadcrumbs = useMemo(
    () => [
      { label: roleLabel, link: "/dashboard" },
      { label: "Freight", link: "/freight" },
      { label: "Social media" }
    ],
    [roleLabel]
  );

  const columnDefs = useMemo(() => COLUMN_DEFS, []);
  const {
    visibility,
    toggleColumn,
    resetColumnsVisible,
    resetColumnOrder,
    orderedColumnDefs,
    onColumnReorder
  } = useFreightAdminTablePrefs(TABLE_ID, columnDefs);
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getFreightSocialMediaIndex();
      setRows(sortRows(freightNamedListFromResponse(res, LIST_KEY)));
    } catch (e) {
      showError(e.message || "Failed to load social links");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const getNavigate = useCallback(() => navigate, [navigate]);

  const handleToggleStatus = useCallback(
    async (row) => {
      if (!row?.id) return;
      const ok = window.confirm(String(row.status) === "1" ? "Deactivate this link?" : "Activate this link?");
      if (!ok) return;
      try {
        const res = await changeFreightSocialMediaStatus({ id: row.id });
        showSuccess(res?.message || "Status updated");
        await load();
      } catch (e) {
        showError(e.message || "Update failed");
      }
    },
    [load]
  );

  const renderActions = useCallback(
    (row) => (
      <FreightSocialMediaDtActions row={row} getNavigate={getNavigate} onToggleStatus={handleToggleStatus} />
    ),
    [getNavigate, handleToggleStatus]
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = [r.name, r.link, r.icon, r.order_no, r.status, r.id].map((x) => String(x ?? "").toLowerCase()).join(" ");
      return hay.includes(q);
    });
  }, [rows, search]);

  return (
    <DashboardLayout className="relative" breadcrumbs={breadcrumbs}>
      <div className="max-w-[1400px]">
        <FreightAdminTableToolbar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Filter by name, link…"
          columnDefs={orderedColumnDefs}
          visibility={visibility}
          onToggleColumn={toggleColumn}
          onResetColumns={resetColumnsVisible}
          onResetColumnOrder={resetColumnOrder}
          rightSlot={
            <Link to="/freight/social_media/new" className="btn-primary no-underline inline-block">
              + New link
            </Link>
          }
        />

        <div className="card offers-table border-l-4 border-l-[#064E8D]">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
            <h3 className="m-0">Freight social media ({filteredRows.length})</h3>
            <button type="button" className="btn-outline text-sm" onClick={() => load()}>
              Refresh
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-3">
            Icons: <strong>jpg, jpeg, png, gif, webp</strong>, max <strong>4 MB</strong>. Optional on create; replace on edit by uploading a new file.
          </p>

          <AdminDataTable
            columnDefs={orderedColumnDefs}
            visibility={visibility}
            rows={filteredRows}
            loading={loading}
            emptyText="No social media entries yet."
            renderActions={renderActions}
            onColumnReorder={onColumnReorder}
          />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FreightSocialMediaPage;
